import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { Custodian } from 'models/custodian';
import type { TradeAllocationFormResult } from 'models/forms/TradeAllocationFormResult';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { Wrapper } from 'models/wrapper';

import type { ApplyResponse, TradeAllocationRowModel } from './tradeAllocationForm.model';

export type TradeAllocationFormAction =
  | TradeAllocationTabCreated
  | TradeAllocationFormRequestActions
  | TradeAllocationFormDataChangeActions;

type TradeAllocationFormRequestActions =
  | TradeAllocationFormRequested
  | TradeAllocationFormRequestFailed
  | TradeAllocationFormRequestTimedOut
  | TradeAllocationFormReceived
  | TradeAllocationFormResetReceived
  | TradeAllocationFormResetRequested
  | TradeAllocationFormResetFailed;

export type TradeAllocationFormDataChangeActions =
  | TradeAllocationNotionalChanged
  | TradeAllocationOfferPriceChanged
  | TradeAllocationXMarginChanged
  | TradeAllocationSponsoChanged
  | TradeAllocationSponsoPercentUpFrontChanged
  | TradeAllocationF2YChanged
  | TradeAllocationTopUpPercentUpfrontChanged
  | TradeAllocationTradingPriceChanged
  | TradeAllocationUpfrontChanged
  | TradeAllocationFightingBackJustificationChanged
  | TradeAllocationFightingBackCommentChanged
  | TradeAllocationSelectedOnBehalfClientChanged
  | TradeAllocationCustodiansReloaded
  | TradeAllocationCustodiansReloadFailed
  | TradeAllocationRowChanged
  | TradeAllocationRowRemoved
  | TradeAllocationApplyRequested
  | TradeAllocationApplyRequestFailed
  | TradeAllocationApplyResponseReceived;

export function tradeAllocationTabCreated(
  tabId: string,
  tradeId: string,
  tabName: string,
  product: string,
  wrapper: Wrapper,
  productId: string,
  rfqId: string,
  isin?: string,
  fileCode?: string,
) {
  return {
    type: 'TRADE_ALLOCATION_TAB_CREATED' as const,
    tabId,
    tradeId,
    tabName,
    product,
    wrapper,
    isin,
    fileCode,
    productId,
    rfqId,
  };
}
export type TradeAllocationTabCreated = ReturnType<typeof tradeAllocationTabCreated>;

export function tradeAllocationFormRequested(
  tabId: string,
  tradeId: string,
  product: string,
  wrapper: Wrapper,
  productId: string,
  rfqId: string,
  isin?: string,
  fileCode?: string,
) {
  return {
    type: 'TRADE_ALLOCATION_FORM_REQUESTED' as const,
    tabId,
    tradeId,
    product,
    wrapper,
    isin,
    fileCode,
    productId,
    rfqId,
  };
}

export type TradeAllocationFormRequested = ReturnType<typeof tradeAllocationFormRequested>;

export function tradeAllocationFormReceived(tabId: string, tradeAllocationFormResult: TradeAllocationFormResult) {
  return {
    type: 'TRADE_ALLOCATION_FORM_RECEIVED' as const,
    tabId,
    tradeAllocationFormResult,
  };
}

export type TradeAllocationFormReceived = ReturnType<typeof tradeAllocationFormReceived>;

export function tradeAllocationFormRequestFailed(tabId: string, tradeId: string, error: string) {
  return {
    type: 'TRADE_ALLOCATION_FORM_REQUEST_FAILED' as const,
    tabId,
    tradeId,
    error,
  };
}

export type TradeAllocationFormRequestFailed = ReturnType<typeof tradeAllocationFormRequestFailed>;

export function tradeAllocationFormRequestTimedOut(tabId: string, tradeId: string) {
  return {
    type: 'TRADE_ALLOCATION_FORM_REQUEST_TIMED_OUT' as const,
    tabId,
    tradeId,
  };
}

export type TradeAllocationFormRequestTimedOut = ReturnType<typeof tradeAllocationFormRequestTimedOut>;

export function tradeAllocationFormResetReceived(tabId: string, tradeAllocationFormResult: TradeAllocationFormResult) {
  return {
    type: 'TRADE_ALLOCATION_FORM_RESET_RECEIVED' as const,
    tabId,
    tradeAllocationFormResult,
  };
}

export type TradeAllocationFormResetReceived = ReturnType<typeof tradeAllocationFormResetReceived>;

export function tradeAllocationFormResetFailed(tabId: string, error: string) {
  return {
    type: 'TRADE_ALLOCATION_FORM_RESET_FAILED' as const,
    tabId,
    error,
  };
}

export type TradeAllocationFormResetFailed = ReturnType<typeof tradeAllocationFormResetFailed>;

export function tradeAllocationFormResetRequested(tabId: string, tradeId: string) {
  return {
    type: 'TRADE_ALLOCATION_FORM_RESET_REQUESTED' as const,
    tabId,
    tradeId,
  };
}

export type TradeAllocationFormResetRequested = ReturnType<typeof tradeAllocationFormResetRequested>;

export function tradeAllocationNotionalChanged(tabId: string, newNotional: number | null) {
  return {
    type: 'TRADE_ALLOCATION_NOTIONAL_CHANGED' as const,
    tabId,
    newNotional,
  };
}

export type TradeAllocationNotionalChanged = ReturnType<typeof tradeAllocationNotionalChanged>;

export function tradeAllocationXMarginChanged(tabId: string, newXMargin: number | undefined) {
  return {
    type: 'TRADE_ALLOCATION_XMARGIN_CHANGED' as const,
    tabId,
    newXMargin,
  };
}

export type TradeAllocationXMarginChanged = ReturnType<typeof tradeAllocationXMarginChanged>;

export function tradeAllocationOfferPriceChanged(tabId: string, newOfferPrice: number | undefined) {
  return {
    type: 'TRADE_ALLOCATION_OFFER_PRICE_CHANGED' as const,
    tabId,
    newOfferPrice,
  };
}

export type TradeAllocationOfferPriceChanged = ReturnType<typeof tradeAllocationOfferPriceChanged>;

export function tradeAllocationTradingPriceChanged(tabId: string, newTradingPrice: number | undefined) {
  return {
    type: 'TRADE_ALLOCATION_TRADING_PRICE_CHANGED' as const,
    tabId,
    newTradingPrice,
  };
}

export type TradeAllocationTradingPriceChanged = ReturnType<typeof tradeAllocationTradingPriceChanged>;

export function tradeAllocationUpfrontChanged(tabId: string, newUpfront: number | undefined) {
  return {
    type: 'TRADE_ALLOCATION_UPFRONT_CHANGED' as const,
    tabId,
    newUpfront,
  };
}

export type TradeAllocationUpfrontChanged = ReturnType<typeof tradeAllocationUpfrontChanged>;

export function tradeAllocationSponsoChanged(tabId: string, newSponso: number | undefined) {
  return {
    type: 'TRADE_ALLOCATION_SPONSO_CHANGED' as const,
    tabId,
    newSponso,
  };
}

export type TradeAllocationSponsoChanged = ReturnType<typeof tradeAllocationSponsoChanged>;

export function tradeAllocationSponsoPercentUpFrontChanged(tabId: string, newSponsoPercentUpFront: number | undefined) {
  return {
    type: 'TRADE_ALLOCATION_SPONSO_PERCENT_UPFRONT_CHANGED' as const,
    tabId,
    newSponsoPercentUpFront,
  };
}

export type TradeAllocationSponsoPercentUpFrontChanged = ReturnType<typeof tradeAllocationSponsoPercentUpFrontChanged>;

export function tradeAllocationF2YChanged(tabId: string, newF2Y: number | undefined) {
  return {
    type: 'TRADE_ALLOCATION_F2Y_CHANGED' as const,
    tabId,
    newF2Y,
  };
}

export type TradeAllocationF2YChanged = ReturnType<typeof tradeAllocationF2YChanged>;

export function tradeAllocationTopUpPercentUpfrontChanged(tabId: string, newTopUpPercentUpfront: number | undefined) {
  return {
    type: 'TRADE_ALLOCATION_TOP_UP_PERCENT_UPFRONT_CHANGED' as const,
    tabId,
    newTopUpPercentUpfront,
  };
}

export type TradeAllocationTopUpPercentUpfrontChanged = ReturnType<typeof tradeAllocationTopUpPercentUpfrontChanged>;

export function tradeAllocationFightingBackJustificationChanged(tabId: string, justification: string | undefined) {
  return {
    type: 'TRADE_ALLOCATION_FIGHTING_BACK_JUSTIFICATION_CHANGED' as const,
    tabId,
    justification,
  };
}

export type TradeAllocationFightingBackJustificationChanged = ReturnType<
  typeof tradeAllocationFightingBackJustificationChanged
>;

export function tradeAllocationFightingBackCommentChanged(tabId: string, comment: string | undefined) {
  return {
    type: 'TRADE_ALLOCATION_FIGHTING_BACK_COMMENT_CHANGED' as const,
    tabId,
    comment,
  };
}

export type TradeAllocationFightingBackCommentChanged = ReturnType<typeof tradeAllocationFightingBackCommentChanged>;

export function tradeAllocationSelectedOnBehalfClientChanged(tabId: string, client: ISalesCounterpart | null) {
  return {
    type: 'TRADE_ALLOCATION_CLIENT_CHANGED' as const,
    tabId,
    client,
  };
}

export type TradeAllocationSelectedOnBehalfClientChanged = ReturnType<
  typeof tradeAllocationSelectedOnBehalfClientChanged
>;

export function tradeAllocationCustodiansReloaded(tabId: string, custodians: Custodian[]) {
  return {
    type: 'TRADE_ALLOCATION_CUSTODIANS_RELOADED' as const,
    tabId,
    custodians,
  };
}

export type TradeAllocationCustodiansReloaded = ReturnType<typeof tradeAllocationCustodiansReloaded>;

export function tradeAllocationCustodiansReloadFailed(tabId: string, error: string) {
  return {
    type: 'TRADE_ALLOCATION_CUSTODIANS_RELOAD_FAILED' as const,
    tabId,
    error,
  };
}

export type TradeAllocationCustodiansReloadFailed = ReturnType<typeof tradeAllocationCustodiansReloadFailed>;

export function tradeAllocationRowChanged(tabId: string, index: number, allocation: TradeAllocationRowModel) {
  return {
    type: 'TRADE_ALLOCATION_ROW_CHANGED' as const,
    tabId,
    index,
    allocation,
  };
}

export type TradeAllocationRowChanged = ReturnType<typeof tradeAllocationRowChanged>;

export function tradeAllocationRowRemoved(tabId: string, index: number) {
  return {
    type: 'TRADE_ALLOCATION_ROW_REMOVED' as const,
    tabId,
    index,
  };
}

export type TradeAllocationRowRemoved = ReturnType<typeof tradeAllocationRowRemoved>;

export function tradeAllocationApplyRequested(tabId: string, startTimeMs: number, estimatedDurationTimeMs: number) {
  return {
    type: 'TRADE_ALLOCATION_APPLY_REQUESTED' as const,
    tabId,
    startTimeMs,
    estimatedDurationTimeMs,
  };
}

export type TradeAllocationApplyRequested = ReturnType<typeof tradeAllocationApplyRequested>;

export function tradeAllocationApplyRequestFailed(tabId: string, technicalError: ApiErrorMessage, cause: any) {
  return {
    type: 'TRADE_ALLOCATION_APPLY_REQUEST_FAILED' as const,
    tabId,
    technicalError,
    cause,
  };
}

export type TradeAllocationApplyRequestFailed = ReturnType<typeof tradeAllocationApplyRequestFailed>;

export function tradeAllocationApplyResponseReceived(tabId: string, response: ApplyResponse) {
  return {
    type: 'TRADE_ALLOCATION_APPLY_RESPONSE_RECEIVED' as const,
    tabId,
    response,
  };
}

export type TradeAllocationApplyResponseReceived = ReturnType<typeof tradeAllocationApplyResponseReceived>;
